import {
    Breadcrumb,
    ContentContainer,
} from "@twinkltech/curriculum-architect-component-library";
import {
    ApiErrorResponse,
    Curriculum,
    Subject,
} from "@twinkltech/curriculum-architect-domain-hooks";
import { useTwinklAnalytics } from "@twinkltech/web-analytics-ts";
import { Text } from "@twinkltech/web-ui";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router-dom";

import { GenericError } from "@/components/GenericError/GenericError";
import { SubjectCard } from "@/components/SubjectCard/SubjectCard";
import { Tag } from "@/components/Tag/Tag";
import { TAG_NS } from "@/components/Tag/Tag.i18n";

import { LoadingPage } from "@/pages/LoadingPage/LoadingPage";

import { COMMON_NS } from "@/i18n/common/common.i18n";
import { paths } from "@/paths";
import { useFeedback } from "@/providers/Feedback/Feedback.context";

import { ActionMenu } from "../CurriculumList/component/ActionMenu/ActionMenu";

import { captureAppError } from "@/utils/monitoring/sentry";
import { SUBJECT_LIST_NS } from "./SubjectList.i18n";


/**
 * Determines the access state of a subject
 * @returns An object with boolean flags for different access states
 */
const getSubjectAccessState = (subject: Subject) => {
  return {
    // User has the required access for this subject
    hasAccess:
      ("userHasAccess" in subject && subject.userHasAccess) ||
      !("userHasAccess" in subject), // treat subjects without access requirements as accessible

    // Subject is marked as coming soon
    isComingSoon: "comingSoon" in subject && subject.comingSoon,
  };
};

export const SubjectList = ({
  curriculum,
  error,
  isLoading,
  isSchoolUser,
  subjectList = [],
}: {
  subjectList: Subject[];
  curriculum?: Curriculum | null;
  error?: ApiErrorResponse | null;
  isSchoolUser?: boolean;
  isLoading: boolean;
}) => {
  const { t } = useTranslation(SUBJECT_LIST_NS);
  const { click } = useTwinklAnalytics();
  const { incrementEngagementCount } = useFeedback();

  const activeSubjectList = subjectList.filter((subject) => {
    if (
      // user has access, or specific access requirements are not needed, and subject is not coming soon
      getSubjectAccessState(subject).hasAccess &&
      !getSubjectAccessState(subject).isComingSoon
    ) {
      return true;
    }

    return false;
  });

  const addOnSubjectList = subjectList.filter((subject) => {
    if (
      // user does not have access, or specific access requirements are not needed, and subject is coming soon
      !getSubjectAccessState(subject).hasAccess &&
      !getSubjectAccessState(subject).isComingSoon
    ) {
      return true;
    }

    return false;
  });

  if (isLoading) {
    return <LoadingPage />;
  } else if (error) {
    captureAppError(error, {
      action: "load",
      area: "subject-list",
    });
    return <GenericError />;
  }

  return (
    <ContentContainer>
      <Breadcrumb className="mb-8">
        <Breadcrumb.Item>
          <Link to={paths.CURRICULUM_LIST_PATH}>
            {t("terms.my-curricula", {
              ns: COMMON_NS,
            })}
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item current className="text-gray-500">
          <span className="truncate max-w-[200px] block">
            {curriculum?.displayLabel}
          </span>
        </Breadcrumb.Item>
      </Breadcrumb>

      <div
        aria-labelledby="overview-title"
        className="mb-10 flex flex-col justify-between gap-2 max-w-3xl"
        role="region"
      >
        <Text
          as="h1"
          className="leading-12"
          id="overview-title"
          variant="3xl-medium"
        >
          {curriculum?.displayLabel}
        </Text>
        {curriculum?.description ? (
          <Text as="p" variant="lg-normal">
            {curriculum?.description}
          </Text>
        ) : null}
      </div>

      <div aria-labelledby="your-subject-title" role="region">
        <div className="flex justify-between items-center mb-5">
          <Text as="h2" id="your-subject-title" variant="xl-medium">
            {t("yourSubject.title")}
          </Text>

          <div
            aria-label="your curriculum statuses"
            className="self-end flex gap-3"
            role="group"
          >
            {isSchoolUser && curriculum?.ownerType && (
              <Tag
                variant={curriculum.ownerType === "USER" ? "private" : "school"}
              >
                <Text variant="base-medium">
                  {curriculum.ownerType === "USER"
                    ? t("private", { ns: TAG_NS })
                    : t("school", { ns: TAG_NS })}
                </Text>
              </Tag>
            )}
            {curriculum?.status && (
              <Tag variant={curriculum.status}>
                <Text variant="base-medium">
                  {t(curriculum.status, { ns: TAG_NS })}
                </Text>
              </Tag>
            )}
            {curriculum?.progress && (
              <Tag variant={curriculum.progress}>
                <Text variant="base-medium">
                  {t(curriculum.progress, { ns: TAG_NS })}
                </Text>
              </Tag>
            )}
            <ActionMenu shouldRedirectOnAction curriculum={curriculum!} />
          </div>
        </div>
        <ul aria-label="active subject list" className="flex flex-col gap-3">
          {activeSubjectList.map((subject) => (
            <li key={subject.id}>
              <SubjectCard
                aria-label={t(`subjectCard.ariaLabel.default`, {
                  subject: subject.label,
                })}
                subject={subject}
                to={generatePath(paths.SUBJECT_PLAN_EDIT_PATH, {
                  curriculum_id: curriculum?.id || "",
                  subject_id: subject.id,
                })}
                onClick={() => {
                  click("click_cta", {
                    ctaCopy: "View & Plan",
                  });
                }}
              />
            </li>
          ))}
        </ul>
      </div>
      {addOnSubjectList.length > 0 && (
        <div aria-labelledby="add-on-subject-title" role="region">
          <Text
            as="h2"
            className="mt-12"
            id="add-on-subject-title"
            variant="xl-medium"
          >
            {t("addOnSubjects.title")}
          </Text>
          <Text as="p" className="mb-4">
            {t("addOnSubjects.description")}
          </Text>
          <ul className="flex flex-col gap-3">
            {addOnSubjectList.map((subject) => (
              <li key={subject.id}>
                <SubjectCard
                  aria-label={t(`subjectCard.ariaLabel.add-on`, {
                    subject: subject.label,
                  })}
                  subject={subject}
                  to={generatePath(paths.EXPLORE_SUBJECT_CONTENTS_SCHEME_PATH, {
                    subject_id: subject.attributes.subjects[0],
                  })}
                  variant="add-on"
                  onClick={() => {
                    incrementEngagementCount();
                  }}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </ContentContainer>
  );
};
